import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Save, X, Heart, MessageCircle } from "lucide-react";
import { admin } from "../api";

const EMPTY = { image: "", caption: "", location: "", likes: 0, comments: 0, hashtags: [] };

export default function PostsPanel() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // post object or 'new'
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      setLoading(true);
      const { data } = await admin.get("/admin/posts");
      setPosts(data);
    } catch (e) {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function save(post) {
    setSaving(true);
    try {
      if (post._new) {
        const payload = { ...post };
        delete payload._new;
        delete payload.id;
        delete payload.order;
        await admin.post("/admin/posts", payload);
        toast.success("Post created");
      } else {
        const { id, ...patch } = post;
        await admin.patch(`/admin/posts/${id}`, patch);
        toast.success("Post updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this post?")) return;
    try {
      await admin.delete(`/admin/posts/${id}`);
      toast.success("Post deleted");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="font-body text-[13px] text-[#FDFBF7]/70">
          {loading ? "Loading…" : `${posts.length} posts`}
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY, _new: true })}
          data-testid="admin-post-new"
          className="pill pill-primary"
        >
          <Plus className="h-4 w-4" /> New post
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {posts.map((p) => (
          <button
            key={p.id}
            onClick={() => setEditing(p)}
            data-testid={`admin-post-${p.id}`}
            className="group relative aspect-square overflow-hidden border border-[#D4AF37]/20 bg-[#1A050B] transition-colors hover:border-[#D4AF37]/70"
          >
            <img src={p.image} alt={p.caption} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent p-3 text-left">
              <div className="line-clamp-2 font-body text-[12px] text-[#FDFBF7]">{p.caption}</div>
              <div className="mt-1 flex items-center gap-3 font-body text-[10px] text-[#D4AF37]">
                <span className="flex items-center gap-1">
                  <Heart className="h-3 w-3" /> {p.likes}
                </span>
                <span className="flex items-center gap-1">
                  <MessageCircle className="h-3 w-3" /> {p.comments}
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {editing && (
          <PostEditor
            key={editing.id || "new"}
            post={editing}
            onCancel={() => setEditing(null)}
            onSave={save}
            onDelete={editing.id && !editing._new ? () => remove(editing.id) : null}
            saving={saving}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function PostEditor({ post, onCancel, onSave, onDelete, saving }) {
  const [form, setForm] = useState({
    ...post,
    hashtags_str: (post.hashtags || []).join(" "),
  });
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  function handleSubmit(e) {
    e.preventDefault();
    const hashtags = form.hashtags_str
      .split(/\s+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => (s.startsWith("#") ? s : `#${s}`));
    onSave({
      ...post,
      image: form.image,
      caption: form.caption,
      location: form.location,
      likes: Number(form.likes) || 0,
      comments: Number(form.comments) || 0,
      hashtags,
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-[#1A050B]/95 backdrop-blur-md p-4"
      onClick={onCancel}
    >
      <motion.form
        initial={{ opacity: 0, scale: 0.98, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.98, y: 12 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="relative w-full max-w-3xl border border-[#D4AF37]/30 bg-[#4A0E1F] p-8 md:p-10"
      >
        <button
          type="button"
          onClick={onCancel}
          data-testid="admin-post-close"
          className="absolute right-4 top-4 rounded-full p-2 text-[#FDFBF7]/70 hover:bg-white/10 hover:text-[#FDFBF7] transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="mb-3 flex items-center gap-3">
          <span className="h-px w-10 bg-[#D4AF37]" />
          <span className="font-body text-[11px] uppercase tracking-[0.36em] text-[#D4AF37]">
            {post._new ? "New post" : `Edit · ${post.id}`}
          </span>
        </div>
        <h2 className="mb-8 font-serif text-3xl leading-none">Compose the frame.</h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-[220px_1fr] md:gap-8">
          <div>
            {form.image ? (
              <img
                src={form.image}
                alt="preview"
                className="aspect-square w-full border border-[#D4AF37]/25 object-cover"
              />
            ) : (
              <div className="flex aspect-square w-full items-center justify-center border border-dashed border-[#D4AF37]/30 text-[#FDFBF7]/40 text-[11px] uppercase tracking-[0.24em]">
                Preview
              </div>
            )}
          </div>
          <div className="space-y-6">
            <label className="block">
              <span className="line-label">Image URL</span>
              <input required value={form.image} onChange={upd("image")} className="line-input" placeholder="https://..." data-testid="admin-post-image" />
            </label>
            <label className="block">
              <span className="line-label">Caption</span>
              <textarea required value={form.caption} onChange={upd("caption")} className="line-input resize-none" rows={2} data-testid="admin-post-caption" />
            </label>
            <label className="block">
              <span className="line-label">Location</span>
              <input value={form.location} onChange={upd("location")} className="line-input" data-testid="admin-post-location" />
            </label>
            <div className="grid grid-cols-2 gap-6">
              <label className="block">
                <span className="line-label">Likes</span>
                <input type="number" min={0} value={form.likes} onChange={upd("likes")} className="line-input" data-testid="admin-post-likes" />
              </label>
              <label className="block">
                <span className="line-label">Comments</span>
                <input type="number" min={0} value={form.comments} onChange={upd("comments")} className="line-input" data-testid="admin-post-comments" />
              </label>
            </div>
            <label className="block">
              <span className="line-label">Hashtags (space-separated)</span>
              <input value={form.hashtags_str} onChange={upd("hashtags_str")} className="line-input" placeholder="#wedding #dubai" data-testid="admin-post-hashtags" />
            </label>
          </div>
        </div>

        <div className="mt-10 flex items-center justify-between">
          {onDelete ? (
            <button
              type="button"
              onClick={onDelete}
              className="pill pill-ghost text-[#F5D97A]/80 hover:text-[#F5D97A]"
              data-testid="admin-post-delete"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onCancel} className="pill pill-ghost">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="pill pill-primary" data-testid="admin-post-save">
              <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </motion.form>
    </motion.div>
  );
}
